import React from "react";
import { STATUS_CFG } from "./constants";
import { fmtDate } from "./utils";
import { renderPlatformIcon } from "../../Automated Crawler/components/PlatformIcon";

const font = "'Host Grotesk', sans-serif";

// ─── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#334155",
        fontFamily: font,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

// ─── Info card wrapper ─────────────────────────────────────────────────────────
function InfoCard({ children }) {
  return (
    <div
      style={{
        padding: "12px 16px",
        backgroundColor: "#ffffff",
        borderRadius: 8,
        border: "1px solid #e2e8f0",
      }}
    >
      {children}
    </div>
  );
}

// ─── Platform block ───────────────────────────────────────────────────────────
function PlatformBlock({ session }) {
  return (
    <InfoCard>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          {renderPlatformIcon(session.platform || "")}
        </div>
        <div>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "#334155",
              fontFamily: font,
              textTransform: "capitalize",
            }}
          >
            {session.platform || "Unknown"}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#64748b",
              fontFamily: font,
              marginTop: 1,
            }}
          >
            {fmtDate(session.dateCreated || session.datetime)}
          </div>
        </div>
      </div>
    </InfoCard>
  );
}

// ─── Status block ─────────────────────────────────────────────────────────────
function StatusBlock({ session, streamConnected, useStream }) {
  const sCfg = STATUS_CFG[String(session.status)] ?? STATUS_CFG["0"];

  return (
    <InfoCard>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 12, color: "#64748b", fontFamily: font }}>
            Status
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11.5,
              fontWeight: 600,
              padding: "3px 10px",
              borderRadius: 6,
              background: sCfg.bg,
              color: sCfg.color,
              fontFamily: font,
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: sCfg.dot,
                flexShrink: 0,
                animation: sCfg.pulse
                  ? "pulse 1.4s ease-in-out infinite"
                  : "none",
              }}
            />
            {sCfg.label}
          </span>
        </div>

        {useStream && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 12, color: "#64748b", fontFamily: font }}>
              Stream
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11.5,
                fontWeight: 600,
                padding: "3px 10px",
                borderRadius: 6,
                background: streamConnected
                  ? "rgba(16,185,129,0.08)"
                  : "rgba(148,163,184,0.08)",
                color: streamConnected ? "#059669" : "#94A3B8",
                fontFamily: font,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: streamConnected ? "#10B981" : "#CBD5E1",
                  animation: streamConnected
                    ? "pulse 1.2s ease-in-out infinite"
                    : "none",
                }}
              />
              {streamConnected ? "Live" : "Idle"}
            </span>
          </div>
        )}

        {session.crawlingServer && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 12, color: "#64748b", fontFamily: font }}>
              Server
            </span>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "#0f766e",
                background: "rgba(15,118,110,0.07)",
                padding: "3px 10px",
                borderRadius: 6,
                fontFamily: font,
              }}
            >
              #{session.crawlingServer}
            </span>
          </div>
        )}

        {session.timeTaken && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 12, color: "#64748b", fontFamily: font }}>
              Duration
            </span>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "#334155",
                fontFamily: font,
              }}
            >
              {session.timeTaken}
            </span>
          </div>
        )}
      </div>
    </InfoCard>
  );
}

// ─── Stats block ──────────────────────────────────────────────────────────────
function StatsBlock({ session }) {
  const stats = [
    {
      val: (session.postCount ?? 0).toLocaleString(),
      lbl: "Crawled Posts",
      color: "#0f766e",
    },
    { val: session.requestedCount ?? "—", lbl: "Requested Posts", color: "#3b82f6" },
    { val: session.section_count ?? "—", lbl: "Unlawful Posts", color: "#ef4444" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {stats.map((s) => (
        <InfoCard key={s.lbl}>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: s.color,
                fontFamily: font,
                letterSpacing: "-0.02em",
                marginBottom: 2,
              }}
            >
              {s.val}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", fontFamily: font }}>
              {s.lbl}
            </div>
          </div>
        </InfoCard>
      ))}
    </div>
  );
}

// ─── Session ID block ─────────────────────────────────────────────────────────
function SessionIdBlock({ sessionId }) {
  return (
    <InfoCard>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "#64748b",
          fontFamily: font,
          marginBottom: 4,
        }}
      >
        Session ID
      </div>
      <div
        style={{
          fontSize: 10.5,
          fontFamily: "monospace",
          color: "#94a3b8",
          wordBreak: "break-all",
          lineHeight: 1.6,
        }}
      >
        {sessionId}
      </div>
    </InfoCard>
  );
}

// ─── Main InfoSidebar ─────────────────────────────────────────────────────────
export function InfoSidebar({
  session,
  sessionId,
  useStream,
  streamConnected,
}) {
  return (
    <div
      style={{
        flex: "0 0 25%",
        display: "flex",
        flexDirection: "column",
        background: "rgba(248,250,252,0.9)",
        borderLeft: "1px solid #e2e8f0",
        overflowY: "auto",
        padding: "24px 16px",
        gap: 12,
      }}
    >
      <SectionLabel>Platform</SectionLabel>
      <PlatformBlock session={session} />

      <SectionLabel>Status</SectionLabel>
      <StatusBlock
        session={session}
        streamConnected={streamConnected}
        useStream={useStream}
      />

      <SectionLabel>Activity</SectionLabel>
      <StatsBlock session={session} />

      <SessionIdBlock sessionId={sessionId} />
    </div>
  );
}
